import { SharedState } from '../index';
import { adapter } from './permission.reducer';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { PolicyEffect, Privilege } from './permission.model';
import { PolicyResponse } from '@chiron/app/shared/graphql';

const selectors = adapter.getSelectors();
const configurationState = createFeatureSelector<SharedState>('shared-lib');
const permissionsState = createSelector(
  configurationState,
  (state) => state.permissions
);
const allPermissions = createSelector(permissionsState, (state) =>
  selectors.selectAll(state)
);
const hasPermissions = (permissions: PolicyResponse[], scope: Privilege) =>
  permissions.find((permission) => permission.id === scope)?.eft !==
  PolicyEffect.Deny;

export const selectPermissionsLoaded = createSelector(
  permissionsState,
  ({ loaded }) => loaded
);

export const selectPermissions = createSelector(
  allPermissions,
  (permissions): Record<Privilege, boolean> => ({
    [Privilege.ReadOpportunities]: hasPermissions(
      permissions,
      Privilege.ReadOpportunities
    ),
    [Privilege.ReadInitiatives]: hasPermissions(
      permissions,
      Privilege.ReadInitiatives
    ),
    [Privilege.ReadInternalOnly]: hasPermissions(
      permissions,
      Privilege.ReadInternalOnly
    ),
    [Privilege.UpdateInternalOnly]: hasPermissions(
      permissions,
      Privilege.UpdateInternalOnly
    ),
    [Privilege.ReadDashboard]: hasPermissions(
      permissions,
      Privilege.ReadDashboard
    ),
    [Privilege.DownloadDashboard]: hasPermissions(
      permissions,
      Privilege.DownloadDashboard
    ),
    [Privilege.ReadKnowledgebase]: hasPermissions(
      permissions,
      Privilege.ReadKnowledgebase
    ),
    [Privilege.UpdateKnowledgebase]: hasPermissions(
      permissions,
      Privilege.UpdateKnowledgebase
    ),
    [Privilege.ReadApollo]: hasPermissions(permissions, Privilege.ReadApollo),
    [Privilege.UpdateApollo]: hasPermissions(
      permissions,
      Privilege.UpdateApollo
    ),
    [Privilege.ReadAdmin]: hasPermissions(permissions, Privilege.ReadAdmin),
    [Privilege.SwitchUsers]: hasPermissions(permissions, Privilege.SwitchUsers),
    [Privilege.ReadEcho]: hasPermissions(permissions, Privilege.ReadEcho),
    [Privilege.ReadRisk]: hasPermissions(permissions, Privilege.ReadRisk),
    [Privilege.ReadPHI]: hasPermissions(permissions, Privilege.ReadPHI),
    [Privilege.ReadLifeline]: hasPermissions(
      permissions,
      Privilege.ReadLifeline
    ),
    [Privilege.ReadStrategyBoard]: hasPermissions(
      permissions,
      Privilege.ReadStrategyBoard
    ),
    [Privilege.ReadTemplates]: hasPermissions(
      permissions,
      Privilege.ReadTemplates
    ),
    [Privilege.UpdateTemplates]: hasPermissions(
      permissions,
      Privilege.UpdateTemplates
    ),
    [Privilege.ReadInternalRoles]: hasPermissions(
      permissions,
      Privilege.ReadInternalRoles
    ),
    [Privilege.ReadFrontdoor]: hasPermissions(
      permissions,
      Privilege.ReadFrontdoor
    ),
    [Privilege.ReadVirtualAnalyst]: hasPermissions(
      permissions,
      Privilege.ReadVirtualAnalyst
    ),
    [Privilege.ReadReportBuilder]:
      hasPermissions(permissions, Privilege.ReadReportBuilder) &&
      hasPermissions(permissions, Privilege.ReadPHI),
    [Privilege.ReadBVA]: hasPermissions(permissions, Privilege.ReadBVA),
    [Privilege.ReadProAxysWorkflow]: hasPermissions(
      permissions,
      Privilege.ReadProAxysWorkflow
    ),
    [Privilege.UpdateStrategyBoard]: hasPermissions(
      permissions,
      Privilege.UpdateStrategyBoard
    ),
    [Privilege.ReadTenantOffboarding]: hasPermissions(
      permissions,
      Privilege.ReadTenantOffboarding
    ),
    [Privilege.RequestTenantOffboarding]: hasPermissions(
      permissions,
      Privilege.RequestTenantOffboarding
    ),
    [Privilege.ApproveTenantOffboarding]: hasPermissions(
      permissions,
      Privilege.ApproveTenantOffboarding
    ),
  })
);
